import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import {
  Container, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, CircularProgress, Alert, Select, MenuItem,
  Box, Pagination, Chip
} from "@mui/material";
import {
  Person as PersonIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Flag as FlagIcon,
  Settings as SettingsIcon,
  Email
} from "@mui/icons-material";
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdvanceSalaryRequestList = () => {
  const [requests, setRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [companyId, setCompanyId] = useState("");
  const itemsPerPage = 10;

  const fetchRequests = useCallback(async () => {
    if (!companyId) return;
    setIsLoading(true);
    setError("");
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error("No authentication token found. Please log in.");
      }
      const response = await axios.get(
        `${API_URL}/api/salary-request?page=${page}&limit=${itemsPerPage}`,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      setRequests(response.data.data || []);
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (err) {
      setError("Failed to fetch advance salary requests. Please try again.");
      setRequests([]);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, page, itemsPerPage]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setCompanyId(payload.company_id);
      } catch (err) {
        //console.error("Invalid token:", err);
      }
    } else {
      setError("No authentication token found. Please log in.");
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (companyId) {
      fetchRequests();
    }
  }, [companyId, fetchRequests]);

  const handleStatusChange = async (
    id,
    newStatus,
    candidateEmail,
    name
  ) => {
    try {
      const token = localStorage.getItem('token');
  
      if (!token) {
        alert("No authentication token found. Please log in.");
        return;
      }
  
      await axios.put(
        `${API_URL}/api/salary-request/${id}/status`,
        {
          status: newStatus,
          candidateEmail,
          name
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );
  
      fetchRequests();
  
    } catch (err) {
      //console.error("Status update error:", err);
  
      if (err.response?.status === 401) {
        alert("Unauthorized. Please login again.");
      } else {
        alert("Failed to update status.");
      }
    }
  };

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved': return 'success';
      case 'Rejected': return 'error';
      default: return 'warning';
    }
  };

  return (
    <Container maxWidth="1500px" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2, border: '2px solid', borderColor: 'grey.300', boxShadow: 4 }}>
        <Typography variant="h4" gutterBottom  fontWeight="bold">
          Advance Salary Request List
        </Typography>

        {isLoading && <CircularProgress sx={{ display: "block", mx: "auto", my: 3 }} />}
        {error && <Alert severity="error">{error}</Alert>}

        {!isLoading && !error && (
          <>
            <TableContainer component={Paper} sx={{ mt: 3 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    {[
                      { icon: <PersonIcon />, text: "Name" },
                      { icon: <BadgeIcon />, text: "Employee ID" },
                      { icon: <Email />, text: "Candidate Email" },
                      { icon: <BusinessIcon />, text: "Department" },
                      { icon: <DescriptionIcon />, text: "Salary Amount" },
                      { icon: <FlagIcon />, text: "Status" },
                      { icon: <SettingsIcon />, text: "Actions" }
                    ].map((header, index) => (
                      <TableCell key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {React.cloneElement(header.icon, { sx: { color: 'black', fontSize: 20 } })}
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'black' }}>
                            {header.text}
                          </Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell>{request.name}</TableCell>
                      <TableCell>{request.employee_id}</TableCell>
                      <TableCell>{request.candidateEmail}</TableCell>
                      <TableCell>{request.department}</TableCell>
                      <TableCell>{request.salaryAmount}</TableCell>
                      <TableCell>
                        <Chip
                          label={request.status || "Pending"}
                          color={getStatusColor(request.status)}
                          variant="outlined"
                          sx={{ 
                            fontWeight: 600,
                            borderWidth: 2,
                            minWidth: 100
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={request.status || "Pending"}
                          onChange={(e) => handleStatusChange(request.id, e.target.value, request.candidateEmail, request.name)}
                          size="small"
                        >
                          <MenuItem value="Pending">Pending</MenuItem>
                          <MenuItem value="Approved">Approve</MenuItem>
                          <MenuItem value="Rejected">Reject</MenuItem>
                        </Select>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default AdvanceSalaryRequestList;